import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam, type ExamState } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { Submission } from '../../submission.js';
import type { GradeTaskTarget } from '../agents/default-grading.js';
import { countCorrectionModeUncertainties } from '../grading-uncertainties.js';
import { createGradingFinishedPendingTask } from '../pending-tasks/grading-finished.task.js';
import { pushWorkflowTask } from '../pending-tasks/workflow-agent.js';
import {
  addressableTaskRefs,
  ambiguousTaskRefMessage,
  resolveRequestedTaskRefs,
} from './resolve-requested-task-refs.js';
import {
  checkGradingSchemeComplete,
  checkLanguageUseSchemeComplete,
  checkMasterSolutionComplete,
} from '../../utils/solution-completeness.js';

export const gradingAgentToolDef = [
  {
    type: 'function',
    function: {
      name: 'run_grading_agent',
      description:
        'Bewertet Schülerabgaben mithilfe der Musterlösung und des Erwartungshorizonts. Nutze dies, wenn der Nutzer bittet, einen Schüler/eine Submission oder alle Schülerabgaben der aktuellen Klausur zu korrigieren/bewerten.' +
        'Wenn der Nutzer auf eine direkte Bewertung besteht und ALLE Schülerabgaben verarbeitet wurden, starte erst die Generierung für die fehlenden Teile (Musterlösung, Erwartungshorizont) und starte direkt die Bewertung in einer Toolnutzungsphase.' +
        'Musterlösung generieren (wenn nötig) -> Bewertungsschema generieren (wenn nötig) -> Bewertung starten -> Antwort an den Nutzer (alles in einer Toolnutzungsphase).',
      parameters: {
        type: 'object',
        properties: {
          submissionId: { type: 'string' },
          allSubmissions: { type: 'boolean' },
          taskNumbers: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Die zu bewertenden Aufgaben als VOLLSTÄNDIGE, punktgetrennte Aufgabennummern von der obersten Ebene an ("2", "2.a", "1.1b.i"). Leer lassen, um die gesamte Klausur zu bewerten. Eine Aufgabe schließt ihre Teilaufgaben immer mit ein. Nennt der Nutzer nur die Bezeichnung einer Teilaufgabe ("a") und kommt diese unter mehreren Aufgaben vor (z.B. 1.a UND 2.a), dann NIEMALS vorsorglich beide eintragen und niemals raten: frage den Nutzer zurück, welche Aufgabe gemeint ist, und rufe dieses Tool erst nach seiner Antwort auf. Mehrere Einträge nur dann, wenn der Nutzer wirklich mehrere Aufgaben genannt hat.',
          },
          target: {
            type: 'string',
            enum: ['content', 'languageUse'],
            description:
              "Worauf die Bewertung sich beschränken soll: 'content' (Standard, falls nicht angegeben) bewertet nur die inhaltliche Richtigkeit, 'languageUse' bewertet die sprachliche Leistung (Grammatik/Rechtschreibung/Sprache) (bewertet dabei automatisch die gesamte verknüpfte Aufgabengruppe, falls die Aufgabe Teil einer gemeinsamen Regel für die sprachliche Leistung ist). Nutze 'languageUse' z.B. wenn der Nutzer explizit eine erneute Bewertung der sprachlichen Leistung (Grammatik) möchte. Ändert NIEMALS das gespeicherte Bewertungsschema — nutze dafür refine_grading_scheme.",
          },
          groupNumber: {
            type: 'integer',
            description:
              'Nur für target languageUse: die kleine Gruppennummer ("Gruppe 3"), die der Nutzer für die sprachliche Leistung genannt hat (z.B. "bewerte die sprachliche Leistung der Gruppe 2 neu"). Regradet dann AUSSCHLIESSLICH diese eine Gruppe, nicht jede Gruppe, die eine der genannten Aufgaben zufällig auch abdeckt. Wenn eine Gruppe direkt über ihre Nummer angesprochen wird, reicht diese Angabe VOLLSTÄNDIG aus — taskNumbers kann dann leer bleiben, die zugehörigen Aufgaben werden automatisch aus der Gruppe übernommen. Niemals raten.',
          },
          languageUseSchemeId: {
            type: 'string',
            description:
              'Fallback für groupNumber, nur wenn eine vorherige Rückfrage genau diese interne ID zur Auswahl genannt hat (Regel ohne Gruppennummer) — genau wie groupNumber reicht dies allein aus, taskNumbers kann leer bleiben. Niemals raten, selbst erzeugen oder dem Nutzer diese ID zeigen.',
          },
        },
      },
    },
  },
];

// Grading uncertainty counting lives in ../grading-uncertainties.js (shared with the reupload
// regrade announcements in announce-regrade.ts).

export async function handleRunGradingAgent(exam: Exam, toolCallArgs: any): Promise<object> {
  let toolResultContent = '';
  const examIdToUse = exam._id?.toString();
  const safeConversationId = String(exam.conversationId ?? '');
  if (!examIdToUse) {
    toolResultContent = 'Fehler: Keine Klausur-ID.';
  } else {
    try {
      // Grading needs both a master solution and a grading scheme. If, for either of them, neither
      // extraction nor generation has even started, there's nothing to grade against — return to the
      // agent (which can have the user upload/generate them, or generate them first per the tool desc).
      const examCollection = await getCollection(Exam);
      const currentExam = await examCollection.findOne({ _id: examIdToUse });
      const state = currentExam?.examState;
      // Anything other than "never ran" counts as started. With one state per artifact, an ERROR is a
      // producer that ran and failed — the completeness gate below then names the tasks it left uncovered,
      // which is a far better message than this gate's "there is no master solution at all".
      const sourceStarted = (status?: StepState): boolean => !!status && status !== StepState.NOT_STARTED;
      if (!sourceStarted(state?.masterSolutionProgress?.status)) {
        return {
          content:
            'SYSTEM INFO: Abbruch. Für diese Klausur ist noch keine Musterlösung vorhanden (weder hochgeladen/extrahiert ' +
            'noch generiert). Bitte den Nutzer auffordern, zuerst eine Musterlösung hochzuladen oder zu generieren, ' +
            'bevor bewertet werden kann.',
        };
      }
      if (!sourceStarted(state?.gradingSchemeProgress?.status)) {
        return {
          content:
            'SYSTEM INFO: Abbruch. Für diese Klausur ist noch kein Bewertungsschema (Erwartungshorizont) vorhanden ' +
            '(weder hochgeladen/extrahiert noch generiert). Bitte den Nutzer auffordern, zuerst ein Bewertungsschema ' +
            'hochzuladen oder zu generieren, bevor bewertet werden kann.',
        };
      }

      // Completeness gate — skipped while a GENERATION IS RUNNING, applied otherwise.
      //
      // It must not key on provenance ("was it uploaded?"): that stopped answering "is it complete?" once
      // generation could fill the gaps of an uploaded solution — an upload covers such a mix while a
      // generation that failed half-way leaves tasks uncovered, the case that most needs gating.
      //
      // Nor may it demand that generation has FINISHED. "Generate what is missing, then grade, all in one
      // tool-use phase" is a supported flow (see this tool's own description): the generation is detached and
      // is still IN_PROGRESS when grading is called, and grading is built for that — its reactive loop grades
      // each task the moment that task's master solution and grading scheme land, bounded by both sources
      // reaching a terminal state. Blocking here would break that chain, which is exactly what it did.
      //
      // So the check applies precisely when the content has stopped changing: generation finished, failed, or
      // never ran. A COMPLETED generation passes it by construction — the agents only report COMPLETED once
      // no gradable task is left without content (their `msTargets` / `gsTargets` check), the same condition
      // the completeness check applies — and a failed or absent one is what we want to catch.
      // A REFINE COUNTS TOO, and must: it drops the units it is redoing (`dropSolutionTasks`) and writes them back
      // when it lands, so mid-refine the artifact genuinely looks incomplete. Checking completeness then would tell
      // the teacher their solution has gaps because a correction they asked for is still running.
      const stillWritingContent = (progress: { status?: StepState; producer?: string } | undefined): boolean =>
        progress?.status === StepState.IN_PROGRESS &&
        (progress.producer === 'generation' || progress.producer === 'refine');
      const msGenerationRunning = stillWritingContent(state?.masterSolutionProgress as any);
      const gsGenerationRunning = stillWritingContent(state?.gradingSchemeProgress as any);
      const target: GradeTaskTarget = toolCallArgs.target === 'languageUse' ? 'languageUse' : 'content';

      // A teacher naming a specific language-use GROUP directly ("bewerte die sprachliche Leistung der
      // Gruppe 2 neu") must regrade ONLY that group's own scheme. Without this, requestedLanguageUseSchemeId
      // stays unset and gradingAgent.ts's schemeGroups filter (see `findLanguageUseSchemesForTask` there)
      // sweeps in every OTHER group sharing a member task with whatever taskNumbers happened to be passed —
      // a displayNumber/`_id` identifies at most one scheme, so this is unambiguous once resolved. Its own
      // `dependsOn` becomes the effective task refs, overriding whatever taskNumbers were passed — same
      // resolution refine_grading_scheme.tool.ts already does for the equivalent case.
      let effectiveTaskNumbers: string[] | undefined = toolCallArgs.taskNumbers;
      let requestedLanguageUseSchemeId: string | undefined;
      const rawGroupNumber = Number(toolCallArgs.groupNumber);
      const hasGroupNumber = Number.isFinite(rawGroupNumber);
      const rawLanguageUseSchemeId =
        typeof toolCallArgs.languageUseSchemeId === 'string' ? toolCallArgs.languageUseSchemeId : undefined;
      if (target === 'languageUse' && (hasGroupNumber || rawLanguageUseSchemeId)) {
        const languageUseSchemeRules: any[] = Array.isArray((currentExam as any)?.languageUseSchemeRules)
          ? (currentExam as any).languageUseSchemeRules
          : [];
        const scheme = languageUseSchemeRules.find(
          s =>
            (hasGroupNumber && Number(s?.displayNumber) === rawGroupNumber) ||
            (rawLanguageUseSchemeId && String(s?._id) === rawLanguageUseSchemeId),
        );
        if (!scheme) {
          return {
            content:
              `Fehler: ${hasGroupNumber ? `Gruppe ${rawGroupNumber}` : 'Die angegebene Regel für die sprachliche Leistung'} wurde im ` +
              'Bewertungsschema nicht gefunden. Bitte die Gruppennummer beim Nutzer noch einmal bestätigen.',
          };
        }
        effectiveTaskNumbers = Array.isArray(scheme.dependsOn) ? scheme.dependsOn : [];
        requestedLanguageUseSchemeId = String(scheme._id);
      }

      // WHICH TASK A TYPED REF MEANS IS DECIDED HERE, not by the regrade dispatch further down — see
      // `resolveRequestedTaskRefs` for why the boundary is the only place that can decide it.
      //
      // `dependsOn` refs are deliberately excluded: they are already "|"-qualified and unambiguous, and
      // running them through resolution would newly hard-fail a group re-grade on a stale member path.
      if (!requestedLanguageUseSchemeId && effectiveTaskNumbers?.length) {
        const resolvedRefs = resolveRequestedTaskRefs((currentExam as any)?.tasks, effectiveTaskNumbers);
        if (resolvedRefs.status === 'ambiguous') {
          logger.warn(
            {
              examId: examIdToUse,
              ref: resolvedRefs.ref,
              candidates: resolvedRefs.candidates,
              context: 'runGradingAgent',
            },
            'Ambiguous task ref — refusing to grade',
          );
          return { content: ambiguousTaskRefMessage(resolvedRefs.ref, resolvedRefs.candidates, 'bewertet') };
        }
        effectiveTaskNumbers = resolvedRefs.refs;
      }

      if (!msGenerationRunning) {
        const { missingTaskPaths } = await checkMasterSolutionComplete(examIdToUse);
        if (missingTaskPaths.length) {
          return {
            content:
              'SYSTEM INFO: Abbruch. Die Musterlösung dieser Klausur ist noch unvollständig — für folgende Aufgaben ' +
              `fehlt sie: ${missingTaskPaths.map(p => `Aufgabe ${p}`).join(', ')}. Biete dem Nutzer beide Wege an: ` +
              'die fehlenden Teile selbst im Canvas ergänzen, oder sie mit generate_master_solution ' +
              'automatisch ergänzen lassen (vorhandene Teile bleiben dabei unverändert).',
          };
        }
      }
      if (!gsGenerationRunning) {
        // Content and grammar are both evaluated regardless of whether the other already failed, and
        // combined into ONE reply — returning early on content alone used to hide a genuine grammar
        // problem on the SAME tasks until the user had already fixed content and asked again (whack-a-mole
        // instead of one complete picture).
        const parts: string[] = [];

        const {
          missingTaskPaths,
          zeroPointsTaskPaths = [],
          missingCriteriaOnlyTaskPaths = [],
        } = await checkGradingSchemeComplete(examIdToUse);
        if (missingTaskPaths.length) {
          // Split by actual cause instead of naming both possible causes for every task — each of these
          // three states needs different (and precise) wording, not a blanket "criteria and/or points":
          //  - criteria present, points <= 0 → only points are missing, canvas-only fix.
          //  - criteria absent, points already > 0 → only criteria are missing, generation can help.
          //  - both absent → genuinely both, generation can help with criteria (points still manual after).
          const zeroPointsSet = new Set(zeroPointsTaskPaths);
          const missingCriteriaOnlySet = new Set(missingCriteriaOnlyTaskPaths);
          const zeroPointsOnly = missingTaskPaths.filter(p => zeroPointsSet.has(p));
          const missingCriteriaOnly = missingTaskPaths.filter(p => missingCriteriaOnlySet.has(p));
          const missingBoth = missingTaskPaths.filter(p => !zeroPointsSet.has(p) && !missingCriteriaOnlySet.has(p));
          if (zeroPointsOnly.length) {
            parts.push(
              `Für ${zeroPointsOnly.map(p => `Aufgabe ${p}`).join(', ')} sind die Kriterien bereits vorhanden, aber ` +
                'die Punktzahl steht auf 0 (oder darunter). Das ist der einzige noch offene Punkt für diese Aufgaben ' +
                '— generate_grading_scheme kann hier NICHT helfen, da es Aufgaben mit vorhandenen Kriterien überspringt. ' +
                'Bitte den Nutzer, die Punktzahl selbst im Canvas zu korrigieren.',
            );
          }
          if (missingCriteriaOnly.length) {
            parts.push(
              `Für ${missingCriteriaOnly.map(p => `Aufgabe ${p}`).join(', ')} fehlen nur die Kriterien — die Punktzahl ` +
                'ist bereits vorhanden. Biete dem Nutzer beide Wege an: die Kriterien selbst im Canvas ergänzen, ' +
                'oder sie mit generate_grading_scheme automatisch ergänzen lassen (vorhandene Punkte bleiben dabei ' +
                'unverändert).',
            );
          }
          if (missingBoth.length) {
            parts.push(
              `Für ${missingBoth.map(p => `Aufgabe ${p}`).join(', ')} fehlen sowohl Kriterien als auch Punkte. Biete ` +
                'dem Nutzer beide Wege an: die fehlenden Teile selbst im Canvas ergänzen, oder sie mit ' +
                'generate_grading_scheme automatisch ergänzen lassen (vorhandene Teile bleiben dabei unverändert). ' +
                'Fehlende PUNKTE kann auch hier nur der Nutzer im Canvas setzen.',
            );
          }
        }

        // Same gate, language-use channel — skipped only when grading is explicitly scoped to content
        // only, since language-use isn't being graded in that case anyway. Computed even if content
        // already failed above, so both show up in the same reply.
        let languageUseIsIncomplete = false;
        if (target !== 'content') {
          const { missingTaskPaths: missingLanguageUseTaskPaths, zeroPointsTaskPaths: zeroLanguageUsePointsPaths = [] } =
            await checkLanguageUseSchemeComplete(examIdToUse);
          if (missingLanguageUseTaskPaths.length) {
            languageUseIsIncomplete = true;
            // Same split as the content gate above — a language-use rubric that EXISTS but sits at
            // maxPoints <= 0 needs a narrower instruction than one that's missing entirely, and
            // generation can't fix the former.
            const zeroLanguageUsePointsSet = new Set(zeroLanguageUsePointsPaths);
            const zeroLanguageUsePointsOnly = missingLanguageUseTaskPaths.filter(p => zeroLanguageUsePointsSet.has(p));
            const missingLanguageUseRubric = missingLanguageUseTaskPaths.filter(p => !zeroLanguageUsePointsSet.has(p));
            if (zeroLanguageUsePointsOnly.length) {
              parts.push(
                `Für ${zeroLanguageUsePointsOnly.map(p => `Aufgabe ${p}`).join(', ')} ist die Regel für die sprachliche ` +
                  'Leistung bereits vorhanden, aber die Punktzahl steht auf 0 (oder darunter). Das ist der einzige ' +
                  'noch offene Punkt — generate_grading_scheme kann hier NICHT helfen, da es Aufgaben mit ' +
                  'vorhandener Regel überspringt. Bitte den Nutzer, die Punktzahl selbst im Canvas zu korrigieren.',
              );
            }
            if (missingLanguageUseRubric.length) {
              parts.push(
                `Für ${missingLanguageUseRubric.map(p => `Aufgabe ${p}`).join(', ')} ist die Regel für die sprachliche ` +
                  'Leistung noch nicht vorhanden. Biete dem Nutzer beide Wege an: die Regel für die sprachliche ' +
                  'Leistung selbst im Canvas ergänzen, oder sie mit generate_grading_scheme automatisch ergänzen ' +
                  'lassen (vorhandene Teile bleiben dabei unverändert).',
              );
            }
          }
        }

        if (parts.length) {
          return {
            content:
              `SYSTEM INFO: Abbruch. Das Bewertungsschema dieser Klausur ist noch unvollständig. ${parts.join(' ')}` +
              (languageUseIsIncomplete
                ? ' Alternativ kann die Bewertung mit target "content" gestartet werden, um vorerst nur den Inhalt zu bewerten.'
                : ''),
          };
        }
      }

      const submissionCollection = await getCollection(Submission);
      const submissions = await submissionCollection.find({ examId: examIdToUse }).toArray();
      if (!submissions || submissions.length === 0) {
        // The only step that genuinely requires submissions. Structure, master solution and grading
        // scheme can all be finished without them, so this is where the teacher gets asked for them.
        toolResultContent =
          'SYSTEM INFO: Abbruch. Für diese Klausur sind keine Schülerabgaben vorhanden — es gibt nichts zu bewerten. ' +
          'Bitte den Nutzer, die Schülerabgaben über die Büroklammer im Chat-Eingabefeld hochzuladen; danach kann die ' +
          'Bewertung starten.';
      } else {
        const targetedSubmissionIds = submissions.map(s => String(s?._id ?? '')).filter(Boolean);
        // Dispatched here rather than inside the background block below, because the tool result names the
        // tasks and must be accurate before it is returned. The jobs are queued at this point, not run —
        // `prepareRegradingRun` is the job's now, so the PENDING reset lands with the work it belongs to.
        const { waitForJobs } = await import('../jobs/dispatch.js');
        const { dispatchGradingWave } = await import('../jobs/dispatch-grading.js');
        // This tool is used for BOTH the exam's first-ever grading AND an explicit regrade of specific
        // tasks/submissions the teacher already asked to redo (e.g. "bewerte Aufgabe 1.1 neu") — only the
        // former is a genuine first grade. `hasGradingStarted` is false exactly until the exam's very
        // first grading run happens and stays true forever after, so it is what distinguishes the two: only
        // then may a solo language-use rule batch into the same 'content' call (see
        // GradeTaskOptions.includeSoloLanguageUse). Once grading has started, an unspecified target must
        // stay content-only — a named regrade must never silently pull in language-use too.
        const isFirstGradingRun = !state?.hasGradingStarted;
        const { jobIds, payloads } = await dispatchGradingWave(examIdToUse, {
          taskRefs: effectiveTaskNumbers,
          submissionIds: targetedSubmissionIds,
          target,
          ...(requestedLanguageUseSchemeId ? { languageUseSchemeId: requestedLanguageUseSchemeId } : {}),
          ...(isFirstGradingRun && target !== 'languageUse' ? { includeSoloLanguageUse: true } : {}),
        });
        // NOTHING WAS QUEUED — stop here rather than reporting a run that does not exist.
        //
        // Every step after this one reports success over zero rows, and each of them is individually correct:
        // the tool result names an empty task list, `waitForJobs` on an empty id list is `done` by definition
        // (nothing outstanding), and the block below then announces a finished re-grade. The teacher is told
        // their submissions were re-graded cleanly when not a single grade was touched.
        //
        // The reachable cause is a task ref that matches nothing — an agent asking for "1.a.i" when the task is
        // numbered "1.1a.i" — so the refs are named back together with the real ones, which is what lets the
        // agent retry correctly instead of reporting success to the user.
        if (jobIds.length === 0) {
          const requested = effectiveTaskNumbers?.length ? effectiveTaskNumbers : undefined;
          const available = addressableTaskRefs((currentExam as any)?.tasks);
          logger.warn(
            { examId: examIdToUse, requested, target, context: 'runGradingAgent' },
            'Grading dispatch matched no task — nothing queued',
          );
          return {
            content:
              'SYSTEM INFO: Abbruch. Es wurde NICHTS bewertet. ' +
              (requested
                ? `Für ${requested.map(ref => `"${ref}"`).join(', ')} gibt es in dieser Klausur keine passende Aufgabe. `
                : 'Es gab keine bewertbare Aufgabe. ') +
              (available.length ? `Vorhandene Aufgaben: ${available.join(', ')}. ` : '') +
              'Melde dem Nutzer NICHT, dass bewertet wurde. Starte die Bewertung mit einer exakten Aufgabennummer ' +
              'aus der obigen Liste erneut, oder frage beim Nutzer nach, welche Aufgabe gemeint ist.',
          };
        }

        const taskNumbersToGrade = payloads.map(payload => payload.taskNumber);

        toolResultContent = `SYSTEM INFO: Die KI-Bewertung für die Aufgaben (${taskNumbersToGrade.join(', ')}) wurde gestartet und läuft im Hintergrund.`;

        // FIRE-AND-FORGET BACKGROUND JOB
        void (async () => {
          try {
            // EVERY ROW OF THE WAVE, not just the content pass: `dispatchGradingWave` returns the
            // `gradingConsistency` and `languageUseGroupGrading` rows too, which is what stops this announcing
            // "fertig" while language-use grading is still running. The type argument only picks the wait budget.
            await waitForJobs('grading', jobIds, examIdToUse);
            const gradedCount = Array.isArray(targetedSubmissionIds) ? targetedSubmissionIds.length : 0;

            const submissionsAfterGrading = await submissionCollection
              .find(
                { _id: { $in: targetedSubmissionIds }, examId: examIdToUse },
                {
                  projection: { taskSubmissions: 1 },
                },
              )
              .toArray();
            const uncertaintyCounts = countCorrectionModeUncertainties(submissionsAfterGrading, taskNumbersToGrade);
            const shouldSuggestCanvas = uncertaintyCounts.total > 0;

            // Push the workflow task; pushWorkflowTask handles processing it — live via a subscribed
            // client's standing subscription, or a final-only fallback when nobody's watching.
            await pushWorkflowTask(
              examIdToUse,
              safeConversationId,
              createGradingFinishedPendingTask({
                taskNumbers: taskNumbersToGrade,
                gradedCount,
                uncertaintiesTotal: uncertaintyCounts.total,
                shouldSuggestCanvas,
              }),
            );
          } catch (bgE) {
            logger.error({ err: bgE }, 'Hintergrund-Grading Fehler');
          }
        })();
      }
    } catch (e) {
      toolResultContent = `Fehler: ${String(e)}`;
    }
  }
  return { content: toolResultContent };
}

export function gradingAgentAvailability(_examState: ExamState): boolean {
  return true;
}
